import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import { base44 } from '@/api/base44Client';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Plus, Database, ChevronRight, Edit2, Trash2, Search, ArrowLeft, ScanLine } from 'lucide-react';
import LastUpdated from '@/components/ui/LastUpdated';
import DataBibitSampleForm from './DataBibitSampleForm';
import BibitScannerModal from './BibitScannerModal';
import { useKurs } from '@/hooks/useKurs';
import { useBatchSelection } from '@/hooks/useBatchSelection';
import BatchActionBar from '@/components/ui/BatchActionBar';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';

export default function DataBibitSampleTab() {
  const qc = useQueryClient();
  const { pathname } = useLocation();
  const isProductionView = pathname.includes('/office/production');
  const { kurs: kursUSD, kursEuro } = useKurs();
  const [showForm, setShowForm] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [search, setSearch] = useState('');
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [selectedGrade, setSelectedGrade] = useState(null);
  const [renameVendor, setRenameVendor] = useState(null);
  const [newVendorName, setNewVendorName] = useState('');

  const { data: list = [], isLoading } = useQuery({
    queryKey: ['bibit-sample'],
    queryFn: () => base44.entities.BibitSample.list('-created_date')
  });

  const delMut = useMutation({
    mutationFn: (id) => base44.entities.BibitSample.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['bibit-sample'] })
  });

  const {
    selectedIds,
    isSelectionMode,
    toggleSelection,
    selectAll,
    clearSelection,
    startSelection,
    selectedCount,
    totalCount
  } = useBatchSelection(list, b => b.id);

  // Group by vendor
  const vendorGroups = useMemo(() => {
    const groups = {};
    list.forEach(item => {
      const v = item.vendor?.trim() || 'Lainnya';
      if (!groups[v]) groups[v] = [];
      groups[v].push(item);
    });
    return groups;
  }, [list]);

  const vendorNames = Object.keys(vendorGroups).sort((a, b) => a.localeCompare(b));

  const {
    selectedIds: selectedVendorIds,
    isSelectionMode: isVendorSelectionMode,
    toggleSelection: toggleVendorSelection,
    selectAll: selectAllVendors,
    clearSelection: clearVendorSelection,
    selectedCount: selectedVendorCount,
    startSelection: startVendorSelection
  } = useBatchSelection(vendorNames, v => v);

  const batchDeleteMutation = useMutation({
    mutationFn: async (ids) => {
      for (const id of ids) {
        await base44.entities.BibitSample.delete(id);
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['bibit-sample'] });
      clearSelection();
    }
  });

  const updateVendorNameMut = useMutation({
    mutationFn: async ({ oldName, newName }) => {
      const items = vendorGroups[oldName] || [];
      const promises = items.map(it => base44.entities.BibitSample.update(it.id, { vendor: newName }));
      await Promise.all(promises);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['bibit-sample'] });
      setRenameVendor(null);
    }
  });

  const handleDeleteVendor = (vendorName) => {
    if (!confirm(`Yakin ingin menghapus vendor "${vendorName}" beserta SEMUA data bibit di dalamnya?`)) return;
    const items = vendorGroups[vendorName] || [];
    batchDeleteMutation.mutate(items.map(it => it.id));
  };


  const filteredList = useMemo(() => {
    if (!selectedVendor) return [];
    let items = vendorGroups[selectedVendor] || [];
    if (selectedVendor.toLowerCase().includes('parfarome') && selectedGrade) {
      items = items.filter(it => it.grade === selectedGrade);
    }
    if (search) {
      const q = search.toLowerCase();
      items = items.filter(it => it.nama_bibit?.toLowerCase().includes(q));
    }
    return items;
  }, [vendorGroups, selectedVendor, selectedGrade, search]);

  const gradesForSelectedVendor = useMemo(() => {
    if (!selectedVendor || !selectedVendor.toLowerCase().includes('parfarome')) return [];
    const items = vendorGroups[selectedVendor] || [];
    const grades = new Set();
    items.forEach(it => { if (it.grade) grades.add(it.grade); });
    return Array.from(grades).sort();
  }, [selectedVendor, vendorGroups]);

  const formatPrice = (item) => {
    if (isProductionView) return null;
    const symbol = item.mata_uang === 'EUR' ? '€' : item.mata_uang === 'USD' ? '$' : 'Rp';
    const formatted = item.harga?.toLocaleString('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    
    if (item.mata_uang !== 'IDR') {
      const idrValue = item.mata_uang === 'EUR' ? item.harga * (kursEuro || 20414) : item.harga * (kursUSD || 17644);
      return (
        <div className="flex flex-col items-end">
          <span className="font-semibold text-sm">{symbol}{formatted}</span>
          <span className="text-[10px] text-muted-foreground mt-0.5">≈ Rp {Math.round(idrValue).toLocaleString('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
        </div>
      );
    }
    return (
      <div className="flex flex-col items-end">
        <span className="font-semibold text-sm">{symbol} {formatted}</span>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {!selectedVendor ? (
        <BatchActionBar
          isSelectionMode={isVendorSelectionMode}
          selectedCount={selectedVendorCount}
          totalCount={vendorNames.length}
          onSelectAll={selectAllVendors}
          onClearSelection={clearVendorSelection}
          onBatchDelete={() => {
            if (confirm(`Yakin ingin menghapus ${selectedVendorCount} vendor terpilih beserta SEMUA bibit di dalamnya?`)) {
              const idsToDelete = [];
              Array.from(selectedVendorIds).forEach(v => {
                const items = vendorGroups[v] || [];
                items.forEach(it => idsToDelete.push(it.id));
              });
              batchDeleteMutation.mutate(idsToDelete);
              clearVendorSelection();
            }
          }}
          isDeleting={batchDeleteMutation.isPending}
        />
      ) : (
        <BatchActionBar
          isSelectionMode={isSelectionMode}
          selectedCount={selectedCount}
          totalCount={filteredList.length}
          onSelectAll={selectAll}
          onClearSelection={clearSelection}
          onBatchDelete={() => {
            if (confirm(`Yakin ingin menghapus ${selectedCount} bibit sample terpilih?`)) {
              batchDeleteMutation.mutate(Array.from(selectedIds));
            }
          }}
          isDeleting={batchDeleteMutation.isPending}
        />
      )}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold">Data Bibit Sample</h2>
          <p className="text-sm text-muted-foreground mt-1">Database bibit dari vendor untuk pembuatan sample</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowScanner(true)} className="gap-2 bg-indigo-50 text-indigo-700 border-indigo-200 hover:bg-indigo-100 hover:text-indigo-800">
            <ScanLine className="w-4 h-4" /> Scan Sample
          </Button>
          <Button onClick={() => { setEditItem(null); setShowForm(true); }} className="gap-2">
            <Plus className="w-4 h-4" /> Tambah Bibit
          </Button>
        </div>
      </div>

      {isLoading ? (
        <div className="text-center py-10 text-muted-foreground">Memuat data...</div>
      ) : !selectedVendor ? (
        vendorNames.length === 0 ? (
          <div className="text-center py-16 border border-dashed border-border rounded-2xl bg-muted/10">
            <Database className="w-10 h-10 mx-auto mb-3 text-muted-foreground opacity-30" />
            <p className="text-muted-foreground text-sm">Belum ada data bibit sample. Klik Tambah untuk memulai.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            {vendorNames.map(v => {
              const items = vendorGroups[v];
              const latest = items.reduce((a, b) => new Date(a.updated_date || 0) > new Date(b.updated_date || 0) ? a : b, items[0]);
              
              return (
                <div key={v} className="relative group">
                  <button onClick={() => { 
                      if (isVendorSelectionMode) {
                        toggleVendorSelection(v);
                      } else {
                        setSelectedVendor(v); setSelectedGrade(null); setSearch('');
                      }
                    }}
                    className={`text-left bg-card border rounded-2xl p-5 transition-all flex flex-col justify-between min-h-[140px] w-full ${isVendorSelectionMode && selectedVendorIds.has(v) ? 'border-rose-500 bg-rose-50/20 shadow-md ring-2 ring-rose-500/20' : 'border-border hover:border-primary/60 hover:shadow-lg'}`}>
                    <div>
                      <div className="flex items-start justify-between mb-2">
                        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                          <Database className="w-5 h-5 text-primary" />
                        </div>
                        {isVendorSelectionMode && (
                          <div className="mt-1" onClick={e => e.stopPropagation()}>
                            <Checkbox checked={selectedVendorIds.has(v)} onCheckedChange={() => toggleVendorSelection(v)} />
                          </div>
                        )}
                      </div>
                    <p className="font-bold text-lg text-foreground mb-1">{v}</p>
                    <div className="text-xs text-muted-foreground">
                      Terakhir update: <LastUpdated date={latest?.updated_date || latest?.created_date} />
                    </div>
                  </div>
                    <div className="flex items-center justify-between mt-4">
                      <div className="flex items-center gap-1 text-xs font-semibold text-primary group-hover:translate-x-1 transition-transform">
                        Lihat Data <ChevronRight className="w-3.5 h-3.5" />
                      </div>
                      <Badge variant="outline" className="bg-background">{items.length} item</Badge>
                    </div>
                  </button>
                  {!isVendorSelectionMode && (
                    <div className="absolute top-4 right-4 flex items-center gap-1 z-10 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="w-8 h-8 text-blue-500/60 hover:text-blue-600 hover:bg-blue-50 rounded-full bg-white/50  shadow-sm"
                      onClick={(e) => { 
                        e.stopPropagation(); 
                        setRenameVendor(v);
                        setNewVendorName(v);
                      }}
                      title="Edit Nama Vendor"
                    >
                      <Edit2 className="w-3.5 h-3.5" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="w-8 h-8 text-rose-500/60 hover:text-rose-600 hover:bg-rose-50 rounded-full bg-white/50  shadow-sm"
                      onClick={(e) => { 
                        e.stopPropagation(); 
                        startVendorSelection(v);
                      }}
                      title="Pilih untuk Dihapus"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                  )}
                </div>
              );
            })}
          </div>
        )
      ) : (
        <div className="space-y-4 animate-in fade-in slide-in-from-bottom-2 duration-300">
          <div className="flex items-center justify-between border-b border-border pb-4">
            <div className="flex items-center gap-3">
              <Button variant="outline" size="icon" onClick={() => setSelectedVendor(null)} className="rounded-xl w-9 h-9">
                <ArrowLeft className="w-4 h-4" />
              </Button>
              <div>
                <div className="text-xs text-muted-foreground font-medium mb-0.5">Menampilkan Database</div>
                <h3 className="text-lg font-bold text-foreground">{selectedVendor}</h3>
              </div>
            </div>
            
            <div className="relative max-w-xs w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Cari nama bibit..."
                className="w-full h-10 pl-9 pr-4 rounded-xl border border-border bg-background text-sm focus:outline-none focus:border-primary transition-colors"
              />
            </div>
          </div>

          {selectedVendor.toLowerCase().includes('parfarome') && gradesForSelectedVendor.length > 0 && (
            <div className="flex gap-2 mb-4">
              <button 
                onClick={() => setSelectedGrade(null)}
                className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all ${!selectedGrade ? 'bg-primary text-primary-foreground shadow-md' : 'bg-muted text-muted-foreground hover:bg-muted/80'}`}>
                Semua Grade
              </button>
              {gradesForSelectedVendor.map(g => (
                <button 
                  key={g} 
                  onClick={() => setSelectedGrade(g)}
                  className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all ${selectedGrade === g ? 'bg-amber-500 text-white shadow-md' : 'bg-amber-50 text-amber-700 hover:bg-amber-100 border border-amber-200'}`}>
                  {g}
                </button>
              ))}
            </div>
          )}

          {filteredList.length === 0 ? (
            <div className="text-center py-10 text-muted-foreground">Tidak ada data bibit ditemukan.</div>
          ) : (
            <div className="grid gap-3">
              {filteredList.map(item => (
                <Card 
                  key={item.id} 
                  className={`transition-all ${
                    isSelectionMode && selectedIds.has(item.id) ? 'ring-2 ring-rose-500 bg-rose-50/20' : 'hover:border-primary/30'
                  }`}
                >
                  <CardContent 
                    className={`p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4 transition-colors ${
                      isSelectionMode ? 'cursor-pointer hover:bg-slate-50' : ''
                    }`}
                    onClick={() => isSelectionMode && toggleSelection(item.id)}
                  >
                    <div className="flex-1 min-w-0 flex items-start gap-3">
                      {isSelectionMode && (
                        <div className="mt-1">
                          <Checkbox checked={selectedIds.has(item.id)} onCheckedChange={() => toggleSelection(item.id)} />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-bold text-base">{item.nama_bibit}</span>
                        {item.status_ketersediaan === 'Tidak Tersedia' ? (
                          <Badge className="bg-rose-100 text-rose-700 hover:bg-rose-200 border-0">Tidak Tersedia</Badge>
                        ) : (
                          <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-200 border-0">Tersedia</Badge>
                        )}
                        {item.ppn_type === 'exclude' ? (
                          <Badge className="bg-rose-50 text-rose-700 border-rose-200 shadow-none">Exclude PPN</Badge>
                        ) : item.ppn_type === 'include' ? (
                          <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 shadow-none">Include PPN</Badge>
                        ) : null}
                      </div>
                      <div className="flex items-center gap-3 text-xs text-muted-foreground">
                        <span className="bg-muted px-2 py-0.5 rounded-md">{item.mata_uang}</span>
                        <LastUpdated date={item.updated_date || item.created_date} />
                      </div>
                      {item.catatan && <p className="text-xs text-muted-foreground mt-2 italic">"{item.catatan}"</p>}
                    </div>
                  </div>
                    <div className="flex items-center justify-between sm:justify-end gap-6 border-t sm:border-t-0 pt-3 sm:pt-0 mt-2 sm:mt-0 border-border">
                      {formatPrice(item)}
                      
                      {!isSelectionMode && (
                        <div className="flex gap-1.5 ml-4">
                          <Button variant="outline" size="icon" className="w-8 h-8 rounded-lg" onClick={(e) => { e.stopPropagation(); setEditItem(item); setShowForm(true); }}>
                            <Edit2 className="w-3.5 h-3.5" />
                          </Button>
                          <Button variant="outline" size="icon" className="w-8 h-8 rounded-lg text-destructive hover:bg-destructive hover:text-white border-destructive/20" 
                            onClick={(e) => { e.stopPropagation(); startSelection(item.id); }}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {showForm && (
        <DataBibitSampleForm 
          item={editItem} 
          defaultVendor={selectedVendor}
          onClose={() => { setShowForm(false); setEditItem(null); }} 
          onSaved={() => { setShowForm(false); setEditItem(null); }}
          hidePrices={isProductionView}
        />
      )}
      {showScanner && (
        <BibitScannerModal
          onClose={() => setShowScanner(false)}
        />
      )}

      {renameVendor && (
        <div className="fixed inset-0 z-[60] bg-black/40  flex items-center justify-center p-4">
          <div className="bg-card rounded-2xl shadow-2xl w-full max-w-sm p-6 animate-in fade-in zoom-in-95 duration-200">
            <h3 className="text-lg font-bold mb-4">Ganti Nama Vendor</h3>
            <div className="space-y-4">
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1 block">Nama Vendor Baru</label>
                <Input 
                  value={newVendorName} 
                  onChange={e => setNewVendorName(e.target.value)} 
                  placeholder="Masukkan nama vendor baru..."
                  autoFocus
                />
              </div>
              <div className="flex items-center gap-3 justify-end pt-4">
                <Button variant="ghost" onClick={() => setRenameVendor(null)} className="rounded-xl">Batal</Button>
                <Button 
                  onClick={() => updateVendorNameMut.mutate({ oldName: renameVendor, newName: newVendorName })}
                  disabled={!newVendorName.trim() || newVendorName === renameVendor || updateVendorNameMut.isPending}
                  className="rounded-xl"
                >
                  {updateVendorNameMut.isPending ? 'Menyimpan...' : 'Simpan Perubahan'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
